/**
 * Pure-data Reel shape: Zod schemas, types, defaults, and layout helpers used
 * by the Hyperframes pipeline, the queue, the API, and the UI.
 *
 * Extracted from the (now removed) Remotion `Reel.tsx` so nothing in the
 * runtime path needs React/Remotion anymore.
 */
import { z } from "zod";
import { ciSchema } from "../brand/schema.js";

const reelSceneSchema = z.object({
  uid: z.string(),
  sceneId: z.string(),
  durationSec: z.number().min(0.5).max(60),
  naturalDurationSec: z.number().min(0.5).max(60).optional(),
  inputs: z
    .object({
      text: z.string().optional(),
      subtext: z.string().optional(),
      number: z.string().optional(),
      unit: z.string().optional(),
      items: z.array(z.string()).optional(),
      media: z.array(z.string()).optional(),
      url: z.string().optional(),
      variant: z.string().optional(),
      accent: z.enum(["primary", "secondary", "accent"]).optional(),
      extras: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    })
    .default({}),
  transition: z
    .object({
      kind: z.enum([
        "none", "crossfade", "wipeLeft", "wipeRight", "slideUp", "slideDown",
        "slideLeft", "slideRight", "zoomIn", "zoomOut", "blurDissolve", "glideUp", "flash",
      ]),
      durationFrames: z.number().min(0).max(45),
    })
    .optional(),
  fromSec: z.number().min(0).max(600).optional(),
  track: z.number().int().min(0).max(8).optional(),
  playbackRate: z.number().min(0.25).max(4).optional(),
});

const overlayBaseShape = {
  uid: z.string(),
  fromSec: z.number().min(0).max(600),
  durSec: z.number().min(0.2).max(60),
  track: z.number().int().min(0).max(20).default(10),
  xPct: z.number().min(-20).max(120).default(50),
  yPct: z.number().min(-20).max(120).default(50),
  scale: z.number().min(0.05).max(8).default(1),
  rotate: z.number().min(-180).max(180).default(0),
  opacity: z.number().min(0).max(1).default(1),
  hidden: z.boolean().optional(),
  locked: z.boolean().optional(),
  name: z.string().optional(),
  parentUid: z.string().optional(),
};

const textOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("text"),
  text: z.string().default(""),
  fontSize: z.number().min(8).max(400).default(72),
  weight: z.number().min(100).max(900).default(800),
  color: z.string().default("primary"),
  align: z.enum(["left", "center", "right"]).default("center"),
  shadow: z.boolean().optional(),
  letterSpacing: z.number().min(-0.1).max(0.5).optional(),
  maxWidthPct: z.number().min(10).max(100).optional(),
});

const shapeOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("shape"),
  shape: z.enum(["rect", "pill", "circle", "star", "arrow", "triangle"]).default("rect"),
  widthPct: z.number().min(0.5).max(120).default(20),
  heightPct: z.number().min(0.5).max(120).default(10),
  fill: z.string().optional(),
  stroke: z.string().optional(),
  strokeWidth: z.number().min(0).max(64).optional(),
  radius: z.number().min(0).max(400).optional(),
});

const iconOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("icon"),
  icon: z.string().default("star"),
  sizePct: z.number().min(1).max(80).default(8),
  color: z.string().optional(),
  strokeWidth: z.number().min(0.5).max(8).optional(),
});

const badgeOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("badge"),
  text: z.string().default("LIVE"),
  icon: z.string().optional(),
  fontSize: z.number().min(8).max(200).default(28),
  fill: z.string().optional(),
  textColor: z.string().optional(),
});

const imageOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("image"),
  src: z.string(),
  widthPct: z.number().min(1).max(120).default(30),
  heightPct: z.number().min(1).max(120).default(30),
  fit: z.enum(["cover", "contain"]).optional(),
  radius: z.number().min(0).max(400).optional(),
});

const groupOverlaySchema = z.object({
  ...overlayBaseShape,
  kind: z.literal("group"),
  expanded: z.boolean().optional(),
});

export const overlayLayerSchema = z.discriminatedUnion("kind", [
  textOverlaySchema,
  shapeOverlaySchema,
  iconOverlaySchema,
  badgeOverlaySchema,
  imageOverlaySchema,
  groupOverlaySchema,
]);

const sfxNameSchema = z.enum([
  "woosh", "swoosh", "impact", "click", "ding", "thud", "pop",
  "riser", "shimmer", "tap", "tick", "notify", "glass",
  "magic", "sub", "transition",
  "sfx495", "sfx503", "sfx505", "sfx676",
]);

export const sfxEventSchema = z.object({
  uid: z.string(),
  fromSec: z.number().min(0).max(600),
  durSec: z.number().min(0.05).max(600),
  sound: sfxNameSchema,
  src: z.string().optional(),
  assetId: z.string().optional(),
  volume: z.number().min(0).max(1).default(0.6).optional(),
  rate: z.number().min(0.25).max(4).default(1).optional(),
  trimStartSec: z.number().min(0).max(600).default(0).optional(),
  fadeInSec: z.number().min(0).max(60).default(0).optional(),
  fadeOutSec: z.number().min(0).max(60).default(0).optional(),
  label: z.string().optional(),
  sceneUid: z.string().optional(),
});

export const reelSchema = z.object({
  scenes: z.array(reelSceneSchema).min(1),
  overlays: z.array(overlayLayerSchema).optional(),
  sfxEvents: z.array(sfxEventSchema).optional(),
  sfxConfig: z
    .object({
      volume: z.number().min(0).max(1).default(1),
      muted: z.boolean().default(false),
    })
    .optional(),
  ci: ciSchema.optional(),
  theme: z.enum(["dark", "light"]).optional(),
  safeZone: z
    .object({
      enabled: z.boolean().default(false),
      platform: z.enum(["tiktok", "reels", "shorts", "stories", "general"]).default("general"),
    })
    .optional(),
  music: z
    .object({
      bed: z.enum(["ambient", "pulse", "riser"]).optional(),
      src: z.string().optional(),
      assetId: z.string().optional(),
      label: z.string().optional(),
      volume: z.number().min(0).max(1).default(0.18),
      enabled: z.boolean().default(true),
      fromSec: z.number().min(0).max(3600).default(0).optional(),
      durSec: z.number().min(0).max(3600).optional(),
      trimStartSec: z.number().min(0).max(3600).default(0).optional(),
      fadeInSec: z.number().min(0).max(60).default(0).optional(),
      fadeOutSec: z.number().min(0).max(60).default(0).optional(),
      loop: z.boolean().optional(),
    })
    .optional(),
  voiceover: z
    .object({
      src: z.string().optional(),
      assetId: z.string().optional(),
      label: z.string().optional(),
      volume: z.number().min(0).max(1).default(1),
      enabled: z.boolean().default(true),
      segments: z
        .array(
          z.object({
            id: z.string(),
            fromSec: z.number().min(0).max(3600),
            durSec: z.number().min(0.05).max(3600),
            trimStartSec: z.number().min(0).max(3600).default(0),
            fadeInSec: z.number().min(0).max(60).default(0).optional(),
            fadeOutSec: z.number().min(0).max(60).default(0).optional(),
            src: z.string().optional(),
            assetId: z.string().optional(),
          }),
        )
        .optional(),
      fromSec: z.number().min(0).max(3600).default(0).optional(),
      durSec: z.number().min(0).max(3600).optional(),
      trimStartSec: z.number().min(0).max(3600).default(0).optional(),
      fadeInSec: z.number().min(0).max(60).default(0).optional(),
      fadeOutSec: z.number().min(0).max(60).default(0).optional(),
    })
    .optional(),
  watermark: z
    .object({
      enabled: z.boolean().default(true),
      position: z.enum(["tl", "tr", "bl", "br"]).default("br"),
      opacity: z.number().min(0).max(1).default(0.78),
      size: z.number().min(16).max(320).default(60),
      paddingPx: z.number().min(0).max(120).default(28),
      offsetX: z.number().min(-50).max(50).default(0).optional(),
      offsetY: z.number().min(-50).max(50).default(0).optional(),
      showOnAllScenes: z.boolean().default(true),
    })
    .optional(),
});

export type ReelProps = z.infer<typeof reelSchema>;

export const reelDefaultProps: ReelProps = {
  scenes: [
    {
      uid: "default-1",
      sceneId: "kineticHook",
      durationSec: 3,
      inputs: { text: "Dein Hook hier", accent: "primary" },
    },
  ],
};

export type ResolvedClip = {
  scene: ReelProps["scenes"][number];
  fromFrames: number;
  durFrames: number;
  track: number;
};

/**
 * Frame-based clip layout used by `calcReelDurationFrames` and any UI that
 * still wants to talk in frames. Matches the legacy Remotion behaviour: when
 * any scene defines `fromSec`, layout is treated as multi-track; otherwise we
 * stack sequentially with transitions consuming overlap.
 */
export function resolveClipLayout(
  scenes: ReelProps["scenes"],
  fps: number,
): ResolvedClip[] {
  const anyExplicit = scenes.some((s) => typeof s.fromSec === "number");
  if (anyExplicit) {
    return scenes.map((s) => ({
      scene: s,
      fromFrames: Math.max(0, Math.round((s.fromSec ?? 0) * fps)),
      durFrames: Math.max(8, Math.round(s.durationSec * fps)),
      track: Math.max(0, Math.round(s.track ?? 0)),
    }));
  }
  let acc = 0;
  const out: ResolvedClip[] = [];
  for (let i = 0; i < scenes.length; i += 1) {
    const s = scenes[i]!;
    const dur = Math.max(8, Math.round(s.durationSec * fps));
    out.push({ scene: s, fromFrames: acc, durFrames: dur, track: 0 });
    acc += dur;
    if (i < scenes.length - 1 && s.transition && s.transition.kind !== "none") {
      acc -= Math.max(0, Math.min(45, s.transition.durationFrames ?? 8));
    }
  }
  return out;
}

export function calcReelDurationFrames(
  scenes: ReelProps["scenes"],
  fps: number,
  overlays?: ReelProps["overlays"],
  sfxEvents?: ReelProps["sfxEvents"],
): number {
  const sceneEnd = scenes.length
    ? resolveClipLayout(scenes, fps).reduce(
        (m, c) => Math.max(m, c.fromFrames + c.durFrames),
        0,
      )
    : 0;
  const overlayEnd = (overlays ?? []).reduce(
    (m, o) => Math.max(m, Math.round((o.fromSec + o.durSec) * fps)),
    0,
  );
  const sfxEnd = (sfxEvents ?? []).reduce(
    (m, s) => Math.max(m, Math.round((s.fromSec + s.durSec) * fps)),
    0,
  );
  return Math.max(60, sceneEnd, overlayEnd, sfxEnd);
}
